import React, { useState, useEffect, useMemo } from "react";
import { Button, Dropdown, Modal, Form, Badge } from "react-bootstrap";
import { Task, Project, TaskStatus, TimeEntry } from "../../types/Work";
import { workStorage } from "../../services/workStorage";
import { KanbanBoard } from "../../components/work/KanbanBoard";
import { TaskModal } from "../../components/work/TaskModal";
import { TaskDetailsModal } from "../../components/work/TaskDetailsModal";
import { ProjectModal } from "../../components/work/ProjectModal";
import { TimerOverlay } from "../../components/work/TimerOverlay";
import { TimeEntriesSection } from "../../components/work/TimeEntriesSection";
import { WorkStatsSection } from "../../components/work/WorkStatsSection";

export default function Work() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [focusMode, setFocusMode] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showDoneHistory, setShowDoneHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | undefined>(undefined);
  const [viewingTask, setViewingTask] = useState<Task | null>(null);
  const [editingProject, setEditingProject] = useState<Project | undefined>(
    undefined,
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksData, projectsData] = await Promise.all([
        workStorage.getTasks(),
        workStorage.getProjects(),
      ]);
      setTasks(tasksData);
      setProjects(projectsData);
    } catch (error) {
      console.error("Failed to load data:", error);
    }
  };

  const filteredTasks = selectedProjectId
    ? tasks.filter((t) => t.projectId === selectedProjectId)
    : tasks;

  const trimmedSearchQuery = searchQuery.trim();

  const getProjectName = (projectId: string | null) => {
    if (!projectId) return "No project";
    return projects.find((p) => p.id === projectId)?.name || "Unknown project";
  };

  const formatStatus = (status: TaskStatus) =>
    status
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "danger";
      case "high":
        return "warning";
      case "medium":
        return "info";
      default:
        return "secondary";
    }
  };

  const searchResults = useMemo(() => {
    const query = trimmedSearchQuery.toLowerCase();
    if (!query) return [];

    return tasks
      .filter((task) => {
        const nameMatch = task.name.toLowerCase().includes(query);
        const descriptionText = task.descriptionHtml
          ? task.descriptionHtml
              .replace(/<[^>]+>/g, " ")
              .replace(/\s+/g, " ")
              .toLowerCase()
          : "";
        const descriptionMatch = descriptionText.includes(query);
        const projectMatch = getProjectName(task.projectId)
          .toLowerCase()
          .includes(query);

        return nameMatch || descriptionMatch || projectMatch;
      })
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }, [tasks, projects, trimmedSearchQuery]);

  const openTaskFromSearch = (taskId: string) => {
    const targetTask = tasks.find((t) => t.id === taskId) || null;
    setViewingTask(targetTask);
    setShowTaskDetailsModal(!!targetTask);
    setShowSearchModal(false);
  };

  // Task operations
  const handleCreateTask = async (
    task: Omit<Task, "id" | "createdAt" | "updatedAt">,
  ) => {
    try {
      await workStorage.createTask(task);
      await loadData();
    } catch (error) {
      console.error("Failed to create task:", error);
    }
  };

  const handleUpdateTask = async (
    task: Omit<Task, "id" | "createdAt" | "updatedAt">,
  ) => {
    if (editingTask) {
      try {
        await workStorage.updateTask(editingTask.id, task);
        await loadData();
        setEditingTask(undefined);
      } catch (error) {
        console.error("Failed to update task:", error);
      }
    }
  };

  const handlePartialUpdateTask = async (
    taskId: string,
    updates: Partial<Task>,
  ) => {
    try {
      await workStorage.updateTask(taskId, updates);
      await loadData();
      // Update viewing task if it's the one being updated
      if (viewingTask?.id === taskId) {
        const updatedTask = await workStorage.getTask(taskId);
        setViewingTask(updatedTask || null);
      }
    } catch (error) {
      console.error("Failed to update task:", error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await workStorage.deleteTask(taskId);
      await loadData();
    } catch (error) {
      console.error("Failed to delete task:", error);
    }
  };

  const handleTaskStatusChange = async (
    taskId: string,
    newStatus: TaskStatus,
  ) => {
    try {
      // Get all tasks in the target column
      const targetColumnTasks = filteredTasks
        .filter((t) => t.status === newStatus)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      // Assign order at the end of the column
      const newOrder =
        targetColumnTasks.length > 0
          ? Math.max(...targetColumnTasks.map((t) => t.order ?? 0)) + 1
          : 0;

      await workStorage.updateTask(taskId, {
        status: newStatus,
        order: newOrder,
      });
      await loadData();
    } catch (error) {
      console.error("Failed to update task status:", error);
    }
  };

  const handleTaskReorder = async (
    taskId: string,
    status: TaskStatus,
    newIndex: number,
  ) => {
    try {
      console.log(
        "Reordering task:",
        taskId,
        "to index:",
        newIndex,
        "in status:",
        status,
      );
      // Get all tasks in this column sorted by order
      const columnTasks = filteredTasks
        .filter((t) => t.status === status)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      // Remove the dragged task from its current position
      const taskIndex = columnTasks.findIndex((t) => t.id === taskId);
      if (taskIndex === -1) return;

      const [movedTask] = columnTasks.splice(taskIndex, 1);

      // Insert at new position
      columnTasks.splice(newIndex, 0, movedTask);

      // Update order for all affected tasks
      const updates = columnTasks.map((task, index) => ({
        id: task.id,
        order: index,
      }));

      // Batch update all tasks
      await Promise.all(
        updates.map((update) =>
          workStorage.updateTask(update.id, { order: update.order }),
        ),
      );

      await loadData();
    } catch (error) {
      console.error("Failed to reorder task:", error);
    }
  };

  const handleEditTaskClick = (task: Task) => {
    setViewingTask(null);
    setShowTaskDetailsModal(false);
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleViewTaskClick = (task: Task) => {
    setViewingTask(task);
    setShowTaskDetailsModal(true);
    // Prevent scroll to bottom when modal opens
    window.scrollTo(0, 0);
  };;

  // Project operations
  const handleCreateProject = async (
    project: Omit<Project, "id" | "createdAt" | "updatedAt">,
  ) => {
    try {
      await workStorage.createProject(project);
      await loadData();
    } catch (error) {
      console.error("Failed to create project:", error);
    }
  };

  const handleUpdateProject = async (
    project: Omit<Project, "id" | "createdAt" | "updatedAt">,
  ) => {
    if (editingProject) {
      try {
        await workStorage.updateProject(editingProject.id, project);
        await loadData();
        setEditingProject(undefined);
      } catch (error) {
        console.error("Failed to update project:", error);
      }
    }
  };

  // Timer operations
  const handleStartTimer = async (taskId: string) => {
    try {
      await workStorage.startTimer(taskId);
      await loadData();
      // Refresh viewing task if it's the one being timed
      if (viewingTask?.id === taskId) {
        const updatedTask = await workStorage.getTask(taskId);
        setViewingTask(updatedTask || null);
      }
    } catch (error) {
      console.error("Failed to start timer:", error);
    }
  };

  const handleStopTimer = async (taskId: string) => {
    try {
      await workStorage.stopTimer(taskId);
      await loadData();
      // Refresh viewing task
      if (viewingTask?.id === taskId) {
        const updatedTask = await workStorage.getTask(taskId);
        setViewingTask(updatedTask || null);
      }
    } catch (error) {
      console.error("Failed to stop timer:", error);
    }
  };

  // Time entry operations
  const handleAddTimeEntry = async (
    taskId: string,
    entry: Omit<TimeEntry, "id">,
  ) => {
    try {
      await workStorage.addTimeEntry(taskId, entry);
      await loadData();
      if (viewingTask?.id === taskId) {
        const updatedTask = await workStorage.getTask(taskId);
        setViewingTask(updatedTask || null);
      }
    } catch (error) {
      console.error("Failed to add time entry:", error);
    }
  };

  const handleUpdateTimeEntry = async (
    taskId: string,
    entryId: string,
    updates: Partial<TimeEntry>,
  ) => {
    try {
      await workStorage.updateTimeEntry(taskId, entryId, updates);
      await loadData();
      if (viewingTask?.id === taskId) {
        const updatedTask = await workStorage.getTask(taskId);
        setViewingTask(updatedTask || null);
      }
    } catch (error) {
      console.error("Failed to update time entry:", error);
    }
  };

  const handleDeleteTimeEntry = async (taskId: string, entryId: string) => {
    try {
      await workStorage.deleteTimeEntry(taskId, entryId);
      await loadData();
      if (viewingTask?.id === taskId) {
        const updatedTask = await workStorage.getTask(taskId);
        setViewingTask(updatedTask || null);
      }
    } catch (error) {
      console.error("Failed to delete time entry:", error);
    }
  };

  // Find currently running timer task
  const runningTimerTask = tasks.find((t) => t.isTimerRunning) || null;
  const doneHistory = useMemo(
    () =>
      filteredTasks
        .filter((task) => task.status === "done")
        .sort(
          (a, b) =>
            (b.completedAt ?? b.updatedAt).getTime() -
            (a.completedAt ?? a.updatedAt).getTime(),
        ),
    [filteredTasks],
  );

  return (
    <div className="work-page">
      {/* Timer Overlay */}
      <TimerOverlay
        task={runningTimerTask}
        onStopTimer={handleStopTimer}
        onViewTask={handleViewTaskClick}
      />

      {/* Header */}
      <div className="work-header">
        <h1>Work</h1>
        <div className="header-actions">
          <Button
            variant="outline-primary"
            onClick={() => {
              setEditingProject(undefined);
              setShowProjectModal(true);
            }}
          >
            <i className="fas fa-folder-plus"></i>
            New Project
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setEditingTask(undefined);
              setShowTaskModal(true);
            }}
          >
            <i className="fas fa-plus"></i>
            New Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="work-filters">
        <span className="filter-label">
          <i className="fas fa-filter"></i>
          Filter by:
        </span>
        <Dropdown>
          <Dropdown.Toggle
            variant="outline-primary"
            size="sm"
            className="project-filter-dropdown"
          >
            <i className="fas fa-folder" style={{ marginRight: "8px" }}></i>
            {selectedProjectId
              ? projects.find((p) => p.id === selectedProjectId)?.name ||
                "Project"
              : "All Projects"}
            <i
              className="fas fa-chevron-down"
              style={{ marginLeft: "8px", fontSize: "10px" }}
            ></i>
          </Dropdown.Toggle>

          <Dropdown.Menu className="project-filter-menu">
            <Dropdown.Item
              onClick={() => setSelectedProjectId(null)}
              active={selectedProjectId === null}
            >
              <i className="fas fa-table-cells"></i>
              All Projects
            </Dropdown.Item>
            <Dropdown.Divider />
            {projects.map((project) => (
              <Dropdown.Item
                key={project.id}
                onClick={() => setSelectedProjectId(project.id)}
                active={selectedProjectId === project.id}
              >
                <span
                  className="project-color-dot"
                  style={{ backgroundColor: project.color }}
                ></span>
                {project.name}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <div className="filter-actions">
          <div
            className={`btn btn-secondary secondary ${focusMode ? "btn-success" : "btn-outline-secondary"} `}
            onClick={() => setFocusMode((value) => !value)}
          >
            <i className="fas fa-bullseye" style={{ marginRight: "8px" }}></i>
            {focusMode ? "Focus mode on" : "Focus mode"}
          </div>
          <div
            className="btn btn-secondary ms-2"
            onClick={() => setShowSearchModal(true)}
          >
            <i className="fas fa-search" style={{ marginRight: "8px" }}></i>
            Search tasks
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      {focusMode && (
        <div className="focus-badge-notice mb-3">
          Focus mode is on. Showing only To Do and In Progress tasks.
        </div>
      )}
      <div className="kanban-container">
        <KanbanBoard
          tasks={filteredTasks}
          projects={projects}
          onTaskStatusChange={handleTaskStatusChange}
          onTaskReorder={handleTaskReorder}
          onEditTask={handleViewTaskClick}
          onDeleteTask={handleDeleteTask}
          onToggleTimer={(task) =>
            task.isTimerRunning
              ? handleStopTimer(task.id)
              : handleStartTimer(task.id)
          }
          onViewDoneHistory={() => setShowDoneHistory(true)}
          focusMode={focusMode}
        />
      </div>

      <WorkStatsSection tasks={tasks} />

      {/* Time Entries Section */}
      <TimeEntriesSection
        tasks={tasks}
        onUpdateTimeEntry={handleUpdateTimeEntry}
        onDeleteTimeEntry={handleDeleteTimeEntry}
        onViewTask={handleViewTaskClick}
      />

      <Modal
        show={showDoneHistory}
        onHide={() => setShowDoneHistory(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Done History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {doneHistory.length === 0 ? (
            <p className="text-muted mb-0">No completed tickets yet.</p>
          ) : (
            <div className="done-history-list">
              {doneHistory.map((task) => (
                <div className="done-history-item" key={task.id}>
                  <div>
                    <strong>{task.name}</strong>
                    <small>
                      Completed{" "}
                      {(task.completedAt ?? task.updatedAt).toLocaleString()} ·{" "}
                      {getProjectName(task.projectId)}
                    </small>
                  </div>
                  <div className="done-history-actions">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        setShowDoneHistory(false);
                        handleViewTaskClick(task);
                      }}
                    >
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => {
                        setShowDoneHistory(false);
                        handleEditTaskClick(task);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => {
                        if (window.confirm(`Delete “${task.name}”?`))
                          handleDeleteTask(task.id);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showSearchModal}
        onHide={() => setShowSearchModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Search Tasks</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            type="search"
            placeholder="Search by name, description, or project"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {trimmedSearchQuery.length === 0 ? (
            <p className="text-muted mt-3 mb-0">
              Start typing to search tasks.
            </p>
          ) : searchResults.length === 0 ? (
            <p className="text-muted mt-3 mb-0">
              No tasks found for "{trimmedSearchQuery}".
            </p>
          ) : (
            <div className="d-grid gap-3 mt-3">
              {searchResults.map((task) => {
                const projectName = getProjectName(task.projectId);
                const descriptionPreview = task.descriptionHtml
                  ? task.descriptionHtml
                      .replace(/<[^>]+>/g, " ")
                      .replace(/\s+/g, " ")
                      .trim()
                  : "";
                const previewText =
                  descriptionPreview.length > 140
                    ? `${descriptionPreview.slice(0, 140)}...`
                    : descriptionPreview;

                return (
                  <div
                    key={task.id}
                    className="border rounded p-3 search-result-card"
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <span className="fw-semibold">{task.name}</span>
                          <Badge bg="secondary">
                            {formatStatus(task.status)}
                          </Badge>
                          <Badge bg="light" text="dark">
                            {projectName}
                          </Badge>
                          <Badge bg={getPriorityVariant(task.priority)}>
                            {task.priority}
                          </Badge>
                        </div>
                        {previewText && (
                          <p className="mb-1 text-muted small">{previewText}</p>
                        )}
                        <div className="text-muted small">
                          <span>
                            Updated: {task.updatedAt.toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex flex-column align-items-end gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openTaskFromSearch(task.id)}
                        >
                          Open
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Modals */}
      <TaskModal
        show={showTaskModal}
        onHide={() => {
          setShowTaskModal(false);
          setEditingTask(undefined);
        }}
        onSave={editingTask ? handleUpdateTask : handleCreateTask}
        task={editingTask}
        projects={projects}
      />

      <TaskDetailsModal
        show={showTaskDetailsModal}
        onHide={() => {
          setShowTaskDetailsModal(false);
          setViewingTask(null);
        }}
        //autoFocus={false}
        task={viewingTask}
        projects={projects}
        onStartTimer={handleStartTimer}
        onStopTimer={handleStopTimer}
        onEditTask={handleEditTaskClick}
        onDeleteTask={handleDeleteTask}
        onUpdateTask={handlePartialUpdateTask}
        onAddTimeEntry={handleAddTimeEntry}
        onUpdateTimeEntry={handleUpdateTimeEntry}
        onDeleteTimeEntry={handleDeleteTimeEntry}
      />

      <ProjectModal
        show={showProjectModal}
        onHide={() => {
          setShowProjectModal(false);
          setEditingProject(undefined);
        }}
        onSave={editingProject ? handleUpdateProject : handleCreateProject}
        project={editingProject}
      />
    </div>
  );
}
